import React, { useState } from 'react';
import { Link, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { IShift, ShiftProviderUser } from 'models';
import { ILeanShift } from '../../BusinessJob/models';
import useLoading from 'library/hooks/useLoading';
import { client } from 'shared';
import GppBadIcon from '@mui/icons-material/GppBad';

interface MarkNoShowButtonProps {
    shift: IShift | ILeanShift;
    provider?: ShiftProviderUser;
    onComplete: () => void;
    providerId: number;
}

export default function MarkNoShowButton({ shift, provider, onComplete, providerId }: MarkNoShowButtonProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Link onClick={() => setIsModalOpen(true)} icon={<GppBadIcon sx={{ fontSize: 18 }} />}>
                Mark as no show
            </Link>
            <NoShowModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onComplete={() => {
                    setIsModalOpen(false);
                    onComplete();
                }}
                shift={shift}
                providerFirstName={provider?.first_name || 'your worker'}
                providerId={providerId}
            />
        </>
    );
}

export function NoShowModal({
    open,
    onClose,
    onComplete,
    shift,
    providerFirstName,
    providerId,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
    shift: IShift | ILeanShift;
    providerFirstName?: string;
    providerId: number;
}) {
    const { loading, setLoading } = useLoading();
    function submit() {
        setLoading(true);
        client(`api/sub-shifts/remove-provider/`, {
            method: 'POST',
            body: {
                comments: 'Business cancelled: no call, no show',
                is_no_show: true,
                appointment_id: shift.id,
                provider_id: providerId,
            },
        })
            .then(onComplete)
            .finally(() => {
                setLoading(false);
                onClose();
            });
    }

    if (!open) return null;

    return (
        <Modal title={`Is ${providerFirstName} not at their shift?`} onClose={onClose}>
            <Stack gap="md">
                <Text>
                    {providerFirstName}&apos;s shift has started and they are not clocked in. Unfortunately, unless{' '}
                    {providerFirstName} is there, this is a no call, no show.
                </Text>
                <Text>
                    We are so sorry for the inconvenience and take no shows very seriously.{' '}
                    <Text tone="warning">
                        Every no show results in the worker losing 75 reliability points and is grounds for immediate
                        termination from our platform.
                    </Text>
                </Text>
                <Row justify="end" fullWidth>
                    <PillButton disabled={loading} onClick={submit}>
                        Confirm No Show
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
